/* =========================================================================
 * The Vitruvian — Speaking Glyph (shared component)
 *
 * ONE animated mark for every "make the character speak" trigger in the Lab.
 * AESTHETICS-ONLY: it never changes a handler, a prompt, or the speech
 * mechanism. It only swaps the visual shell of a trigger and reflects four
 * states from a single motion source:
 *
 *   IDLE      staggered breath + continuous glow + overlapping summon rings
 *   PRESSED   stamp on tap
 *   THINKING  deeper breath with a tilt (on hover, and between tap & audio)
 *   SPEAKING  scales/glows with the REAL voice, rings radiate
 *
 * SPEAKING IS KEYED TO window.TTSActive (NOT window.tts.playing). `playing`
 * is the decode-loop flag — ElevenLabs delivers faster than realtime, so it
 * flips false seconds before the voice actually stops, which made speaking
 * vanish ~1s in. `window.TTSActive` is the module-global "audio is audible"
 * flag the engine sets when the first chunk is scheduled and clears at the
 * real end-of-utterance teardown. It is also engine-instance-agnostic, so it
 * covers EVERY speech path (AI sendMessageSilently, direct speakDirectly,
 * the auto-greeting, plain chat) with one signal.
 *
 * While the engine is speaking, EVERY currently-visible glyph reflects
 * SPEAKING — the user expects the speak triggers to react whenever the
 * character speaks, not only the one button that was clicked. They all read
 * the same global --amp, so they pulse with the voice in unison.
 *
 * SPEAKING amplitude comes from ONE passive AnalyserNode tapped off the
 * engine's masterGain (window.tts.masterGain).
 *
 * AUDIO GUARDRAIL: the analyser is a read-only side branch. masterGain still
 * drives the speaker untouched; the analyser is NEVER connected onward to the
 * destination, nothing is ever paused/suspended. If amplitude is unreadable
 * the glyph simply rests at a low --amp — playback is never affected.
 *
 * Render modes (chosen by the trigger's existing form, like-for-like):
 *   button  text trigger → glyph injected before the existing label
 *   icon    icon-only trigger → glyph replaces the icon one-for-one
 *   badge   image trigger → badge bottom-right, image is the tap target
 *
 * Motion spec is faithful to (and bolder than) the calm showcase page
 * attached_assets/vitruvian-glyph_1781229444824.html.
 * ========================================================================= */

(function () {
  "use strict";

  const REDUCE = typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const PRESS_MS = 230;          // tap stamp duration
  const AWAIT_MS = 18000;        // show "thinking" this long waiting for audio
  const SPEAK_RELEASE_MS = 260;  // hold speaking briefly after TTSActive clears

  function glyphInner(withTwoRings) {
    const r2 = withTwoRings ? '<span class="vring v2"></span>' : "";
    return '<span class="vring v1"></span>' + r2 + '<span class="vman"></span>';
  }

  const Manager = {
    hosts: new Set(),       // every decorated trigger
    _amp: 0.04,
    _target: 0.04,
    _analyser: null,
    _ampBuf: null,
    _tapCtx: null,
    _tapGain: null,
    _started: false,
    _hoverCapable: true,
    _wasSpeaking: false,
    _quietSince: 0,

    init() {
      if (this._started) return;
      this._started = true;

      this._hoverCapable =
        typeof window.matchMedia !== "function" ||
        window.matchMedia("(hover: hover)").matches;

      // Auto-decorate anything already in the DOM, then watch for more.
      this.scan(document);
      try {
        const mo = new MutationObserver((muts) => {
          for (const m of muts) {
            for (const node of m.addedNodes) {
              if (node.nodeType !== 1) continue;
              if (node.matches && node.matches("[data-vglyph]")) this.decorate(node);
              if (node.querySelectorAll) this.scan(node);
            }
          }
        });
        mo.observe(document.body || document.documentElement, {
          childList: true,
          subtree: true,
        });
        this._mo = mo;
      } catch (_) {}

      requestAnimationFrame(this._frame.bind(this));
    },

    scan(root) {
      try {
        const els = root.querySelectorAll("[data-vglyph]:not([data-vglyph-ready])");
        els.forEach((el) => this.decorate(el));
      } catch (_) {}
    },

    /**
     * Turn a marked trigger into a glyph host. Idempotent.
     * Markup contract: the element carries data-vglyph="button|icon|badge".
     */
    decorate(el) {
      if (!el || el.getAttribute("data-vglyph-ready") === "1") return;
      const mode = el.getAttribute("data-vglyph") || "button";
      el.setAttribute("data-vglyph-ready", "1");
      el.setAttribute("data-vstate", "idle");
      // small stagger so a panel of glyphs never pulses in unison (kept short
      // so the first summon ring/sheen is visible within ~2s, not ~6s).
      el.style.setProperty("--stagger", (Math.random() * 2.2).toFixed(2) + "s");

      if (mode === "badge") {
        if (!el.querySelector(":scope > .vbadge")) {
          const badge = document.createElement("span");
          badge.className = "vbadge";
          badge.setAttribute("aria-hidden", "true");
          badge.innerHTML = glyphInner(false);
          if (getComputedStyle(el).position === "static") el.style.position = "relative";
          el.appendChild(badge);
        }
      } else if (mode === "icon") {
        const wrap = document.createElement("span");
        wrap.className = "vwrap";
        wrap.setAttribute("aria-hidden", "true");
        wrap.innerHTML = glyphInner(true);
        el.innerHTML = "";
        el.appendChild(wrap);
      } else {
        // button mode — inject before the label
        if (!el.querySelector(":scope > .vwrap")) {
          const wrap = document.createElement("span");
          wrap.className = "vwrap";
          wrap.setAttribute("aria-hidden", "true");
          wrap.innerHTML = glyphInner(true);
          el.insertBefore(wrap, el.firstChild);
        }
      }

      this.hosts.add(el);

      // The pre-existing handler still runs (we never preventDefault); we only
      // drive visuals: a tap shows pressed → thinking until audio is audible.
      const onActivate = () => this._onActivate(el);
      el.addEventListener("click", onActivate);
      el.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") this._onActivate(el);
      });

      // Thinking-on-hover (pointer devices only; never on touch).
      if (this._hoverCapable) {
        el.addEventListener("mouseenter", () => { el._vgHover = true; });
        el.addEventListener("mouseleave", () => { el._vgHover = false; });
      }
    },

    _onActivate(el) {
      if (!el) return;
      const now = performance.now();
      el._vgPressUntil = now + PRESS_MS;
      el._vgAwaitUntil = now + AWAIT_MS; // waiting for audio → show thinking
    },

    _ensureTap() {
      const tts = window.tts;
      if (!tts || !tts.audioCtx || !tts.masterGain) return false;
      if (this._tapCtx === tts.audioCtx && this._tapGain === tts.masterGain && this._analyser) {
        return true;
      }
      try {
        const an = tts.audioCtx.createAnalyser();
        an.fftSize = 1024;
        an.smoothingTimeConstant = 0.6;
        // PASSIVE branch: read-only. masterGain keeps driving the speaker; the
        // analyser is intentionally NOT connected onward to the destination.
        tts.masterGain.connect(an);
        this._analyser = an;
        this._ampBuf = new Uint8Array(an.fftSize);
        this._tapCtx = tts.audioCtx;
        this._tapGain = tts.masterGain;
        return true;
      } catch (_) {
        this._analyser = null;
        return false;
      }
    },

    _readAmplitude() {
      if (!this._ensureTap()) return null;
      try {
        this._analyser.getByteTimeDomainData(this._ampBuf);
        const buf = this._ampBuf;
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        if (!isFinite(rms)) return null;
        return rms;
      } catch (_) {
        return null;
      }
    },

    _setState(el, st) {
      if (el.getAttribute("data-vstate") !== st) el.setAttribute("data-vstate", st);
    },

    // Only meaningful while speaking — keep it cheap (called per host per frame
    // ONLY when the engine is speaking).
    _isVisible(el) {
      if (!el || !el.isConnected) return false;
      if (el.offsetParent === null && getComputedStyle(el).position !== "fixed") {
        return false; // display:none somewhere up the tree
      }
      const r = el.getBoundingClientRect();
      if (r.width < 1 || r.height < 1) return false;
      const vh = window.innerHeight || document.documentElement.clientHeight || 0;
      const vw = window.innerWidth || document.documentElement.clientWidth || 0;
      if (r.bottom < 0 || r.top > vh || r.right < 0 || r.left > vw) return false;
      return true;
    },

    _frame(t) {
      const root = document.documentElement;

      if (REDUCE) {
        root.style.setProperty("--amp", "0.45");
        requestAnimationFrame(this._frame.bind(this));
        return;
      }

      // The single source of truth for "the character is audibly speaking".
      const raw = !!window.TTSActive;
      let speaking = raw;
      if (raw) {
        this._quietSince = 0;
      } else if (this._wasSpeaking) {
        // Brief hold so the very end of a clip doesn't snap to idle.
        if (!this._quietSince) this._quietSince = t;
        if (t - this._quietSince < SPEAK_RELEASE_MS) speaking = true;
      }
      this._wasSpeaking = speaking;

      // Amplitude → global --amp (read whenever speaking; every speaking glyph
      // shares it). No `playing` gate — that was the bug.
      if (speaking) {
        const rms = this._readAmplitude();
        this._target = rms == null
          ? 0.4 // unreadable amplitude: keep it visibly alive, never block audio
          : Math.min(1, Math.max(0.28, rms * 6));
      } else {
        this._target = 0.04;
      }
      this._amp += (this._target - this._amp) * 0.28;
      root.style.setProperty("--amp", this._amp.toFixed(3));

      // Reconcile every glyph's state.
      const now = performance.now();
      this.hosts.forEach((el) => {
        if (!el.isConnected) { this.hosts.delete(el); return; }
        if (speaking) el._vgAwaitUntil = 0; // audio arrived → consume the wait

        let st;
        if (speaking && this._isVisible(el)) {
          st = "speaking";
        } else if (now < (el._vgPressUntil || 0)) {
          st = "pressed";
        } else if (now < (el._vgAwaitUntil || 0)) {
          st = "thinking";
        } else if (el._vgHover) {
          st = "thinking";
        } else {
          st = "idle";
        }
        this._setState(el, st);
      });

      requestAnimationFrame(this._frame.bind(this));
    },
  };

  function boot() {
    Manager.init();
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  window.SpeakingGlyph = {
    decorate: (el) => Manager.decorate(el),
    scan: (root) => Manager.scan(root || document),
    _manager: Manager,
  };
})();
